import React, { useImperativeHandle, forwardRef } from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  Popover,
  Box,
  IconButton,
  TextField,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddIcon from "@mui/icons-material/Add";
import { Button, Typography } from "@platform/design-system";

import { BRAND_PURPLE } from "./editorColors";
import { useAgeReductionEditor } from "./useAgeReductionEditor";
import {
  AgeReductionEntry,
  getValidationErrorMessage,
  isDoneButtonEnabled,
} from "./ageReductionUtils";

export interface AgeReductionScheduleEditorRef {
  getValue: () => Omit<AgeReductionEntry, "id">[];
}

/**
 * Specialized editor for Age Reduction Schedules
 *
 * Provides validation for age reduction schedule entries with the following rules:
 * - At least one entry is required at all times
 * - Both reductionPercent (1-100) and age (1-120) must be valid numbers
 * - Empty entries (both fields empty) are filtered out on save
 * - Visual validation feedback on blur
 * - Done button disabled until all entries are valid
 *
 * Note: While the HTML input allows min="0" for better UX (allows deleting digits),
 * the actual validation requires values >= 1. This prevents the browser from blocking
 * deletion of single-digit values (e.g., deleting "6" to type "65").
 */
export const AgeReductionScheduleEditor = forwardRef<
  AgeReductionScheduleEditorRef,
  CustomCellEditorProps
>((props, ref) => {
  const {
    entries,
    open,
    anchorRef,
    isDisabled,
    handleClose,
    handleDone,
    handleAddEntry,
    handleRemoveEntry,
    handleFieldChange,
    handleFieldBlur,
    shouldShowFieldError,
  } = useAgeReductionEditor(props);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getValue: () => entries.map(({ id, ...rest }) => rest), // Strip out ID before returning
  }));

  const renderAgeReductionEntry = (
    entry: AgeReductionEntry,
    index: number,
  ): React.JSX.Element => {
    const percentValue = entry.reductionPercent;
    const ageValue = entry.age;

    const percentError = shouldShowFieldError(
      index,
      "reductionPercent",
      percentValue,
    );
    const ageError = shouldShowFieldError(index, "age", ageValue);

    const showDeleteButton = entries.length > 1;

    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
        <TextField
          type="number"
          value={percentValue}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange(index, "reductionPercent", e.target.value);
          }}
          onBlur={(e) => {
            e.stopPropagation();
            handleFieldBlur(index, "reductionPercent");
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          size="small"
          placeholder="%"
          inputProps={{
            // Note: min is set to 0 (not 1) to allow better UX when deleting digits.
            // This prevents the browser from blocking deletion of single-digit values.
            // However, actual validation requires values >= 1 (enforced on blur).
            min: 0,
            max: 100,
            step: 1,
          }}
          sx={{ width: "80px" }}
          autoComplete="off"
          error={percentError}
        />
        <Typography variant="body-medium" sx={{ flexShrink: 0 }}>
          % at
        </Typography>
        <TextField
          type="number"
          value={ageValue}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange(index, "age", e.target.value);
          }}
          onBlur={(e) => {
            e.stopPropagation();
            handleFieldBlur(index, "age");
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          size="small"
          placeholder="age"
          inputProps={{
            // Note: min is set to 0 (not 1) to allow better UX when deleting digits.
            // This prevents the browser from blocking deletion of single-digit values.
            // However, actual validation requires values >= 1 (enforced on blur).
            min: 0,
            max: 120,
            step: 1,
          }}
          sx={{ width: "80px" }}
          autoComplete="off"
          error={ageError}
        />
        {showDeleteButton && (
          <IconButton
            size="small"
            onClick={() => handleRemoveEntry(index)}
            sx={{ mt: 0.5 }}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  // Don't render popover if disabled
  if (isDisabled) {
    return <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />;
  }

  const errorMessage = getValidationErrorMessage(entries);
  const doneButtonEnabled = isDoneButtonEnabled(entries);

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 400,
              maxWidth: 600,
              maxHeight: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }} onClick={(e) => e.stopPropagation()}>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {entries.map((entry, index) => (
              <Box key={entry.id}>
                {renderAgeReductionEntry(entry, index)}
                {index < entries.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>

          <Button
            variant="secondary"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            fullWidth
            disabled={entries.length >= 10}
            sx={{
              borderStyle: "dashed",
              borderColor: BRAND_PURPLE,
              color: BRAND_PURPLE,
            }}
          >
            Add Option
          </Button>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={handleDone}
              variant="primary"
              disabled={!doneButtonEnabled}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

AgeReductionScheduleEditor.displayName = "AgeReductionScheduleEditor";
